// ABOUTME: Editors for the naheffingen import options kept per shipper: the descriptions to
// ABOUTME: skip, and the descriptions invoiced on an invoiceproduct of their own
import { Box, IconButton, InputBase, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { tokens } from "../../../theme";
import Shbutton from "../../theme/buttons/Shbutton";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { InputDropdown } from "../../theme/dropdowns/InputDropdown";

// The options as the API stores them, from the values of the import modal: blank rows and
// unfinished mappings are left out.
export const buildAdjustmentChargeOptions = (values) => ({
    skip: (values.skip ?? []).map(line => (line ?? "").trim()).filter(line => line.length > 0),
    products: (values.products ?? [])
        .filter(row => row.description?.trim() && row.invoiceProductId)
        .map(row => ({ description: row.description.trim(), invoiceProductId: row.invoiceProductId })),
    includeReturns: values.includeReturns === true
});

// A row of the same shape as the barcode rows on the stock details panel: the fields the row
// carries, each in its own pill, and the delete button at the end.
const OptionRow = ({ children, onDelete }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: 1,
            }}
        >
            {children}
            <IconButton type="button" onClick={onDelete}
                sx={{
                    color: colors.txt["primary"]
                }}>
                <DeleteIcon/>
            </IconButton>
        </Box>
    );
};

const NewRowButton = ({ onClick }) => {
    const { t } = useTranslation();

    return (
        <Box>
            <ButtonGroup version={2}>
                <Shbutton className={"group-btn"} color={"green"}
                    variant="contained"
                    startIcon={<AddIcon/>}
                    onClick={onClick}
                >{t("new")}</Shbutton>
            </ButtonGroup>
        </Box>
    );
};

const inputSx = (theme, colors) => ({
    flex: 1,
    backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
    borderRadius: 32 / 8,
    display: "flex",
    height: "32px",
});

// value: [description], one row per description to leave out of the import
export const AdjustmentChargeSkipList = ({ value, onChange }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const rows = Array.isArray(value) ? value : [];

    return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            {rows.map((description, index) => (
                <OptionRow key={"ac-skip" + index} onDelete={() => onChange(rows.filter((_, i) => i !== index))}>
                    <Box sx={inputSx(theme, colors)}>
                        <InputBase
                            type="text"
                            sx={{
                                px: 2,
                                flex: 1,
                                color: colors.txt["primary"],
                                lineHeight: "32px",
                            }}
                            value={description ?? ""}
                            onChange={(e) => onChange(rows.map((row, i) => i === index ? e.target.value : row))}
                        />
                    </Box>
                </OptionRow>
            ))}
            <NewRowButton onClick={() => onChange([...rows, ""])} />
        </Box>
    );
};

// value: [{ description, invoiceProductId }], invoiceProducts: [{ id, name }]
export const AdjustmentChargeProductMapping = ({ value, onChange, invoiceProducts }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const rows = Array.isArray(value) ? value : [];
    const products = invoiceProducts ?? [];

    const updateRow = (index, changes) => onChange(rows.map((row, i) => i === index ? { ...row, ...changes } : row));

    return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            {rows.map((row, index) => (
                <OptionRow key={"ac-product" + index} onDelete={() => onChange(rows.filter((_, i) => i !== index))}>
                    <Box sx={inputSx(theme, colors)}>
                        <InputBase
                            type="text"
                            sx={{
                                px: 2,
                                flex: 1,
                                color: colors.txt["primary"],
                                lineHeight: "32px",
                            }}
                            placeholder={t("description")}
                            value={row.description ?? ""}
                            onChange={(e) => updateRow(index, { description: e.target.value })}
                        />
                    </Box>
                    {/* The dropdown fills its container, so it gets a box of its own beside the
                        description rather than sharing one with it. */}
                    <Box sx={{ flex: 1, display: "flex" }}>
                        <InputDropdown
                            name="name"
                            displayName="name"
                            selected={products.findIndex(product => product.id === row.invoiceProductId)}
                            options={products}
                            noSelection={t("choose_option")}
                            onChange={(selected) => updateRow(index, { invoiceProductId: products[selected]?.id ?? null })}
                            closeOnSelection
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search")
                            }}
                            styles={{
                                minWidth: 220
                            }}
                            disallowDeselection
                        />
                    </Box>
                </OptionRow>
            ))}
            <NewRowButton onClick={() => onChange([...rows, { description: "", invoiceProductId: null }])} />
        </Box>
    );
};
